/* eslint-disable jsx-a11y/control-has-associated-label */
import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { DropTarget as dropTarget } from 'react-dnd';
import * as cnsts from '../AutocodeConstants';
import GenericButton from '../../common/GenericButton';
import AutocodeSelectionAction from './AutocodeSelectionActions';
import {
  AC_NODETYPE_DATAPOINT,
  AC_NODETYPE_OPERATOR,
  AC_NODETYPE_FUNC,
  AC_TEMPLATE_2_7_8_ARR,
} from '../AutocodeConstants';
import { useACSelectionBoxContext } from '../ACContext/ACSelectionBoxContext';
import { useACEditContext } from '../ACContext/ACEditContext';
import AutocodeSelectionBoxItems from './AutocodeSelectionBoxItems';
import GenericEditWithCancelSave from '../../common/GenericEditWithCancelSave';
import InfoBox from '../../common/EnhancedGridFeature/InfoBox';
import {
  funcsInsideFX,
  CanCombineSelectedAsNetAny,
} from '../AutocodeFunctions';
import ACFunctionBoxPanel from './ACFunctionBoxPanel';
import AutocodeCustomDragLayer from '../AutocodeCustomDragLayer';
import GenericCheckBox from '../../common/GenericCheckBox';
import AC_GRID from '../AutocodeTypes';
import {
  AUTOCODE_CANCEL_BULK_JOIN,
  AUTOCODE_FUNCTION_BTN_CLICK,
  AUTOCODE_FUNCTION_COUNT_EXACTLY_RADIO_CLICK,
  AUTOCODE_FUNCTION_COUNT_MINMAX_RADIO_CLICK,
  AUTOCODE_SETUP_BULK_JOIN,
} from '../../../constants';

const target = {
  canDrop(props, monitor) {
    return monitor.getItem() && props;
  },
  drop(props, monitor) {
    if (monitor.getItem().ACTemplateType) {
      if (monitor.getItem().ACTemplateType === 'ACDefinitions') {
        props.addDefToSelectionBox();
      } else {
        props.onDictionaryDrop(
          monitor.getItem(),
          monitor.getItem().ACTemplateType
        );
      }
    } else if (props.onSelectionBoxDrop) {
      props.onSelectionBoxDrop(monitor.getItem(), monitor.getItemType());
    }
  },
};

const collect = (connect, monitor) => ({
  connectDropTarget: connect.dropTarget(),
  isOver: monitor.isOver(),
  canDrop: monitor.canDrop(),
});

const AutocodeMainSelectionBox = ({
  numTopLevelNodes,
  selectedNodesCount,
  selectedOperatorCount,
  checkApplyAllOr,
  handleDeleteClicked,
  definitionTab,
  tempOverridelabels,
  groupWithOn,
  setGroupWithOn,
  moduleStudy,
  setCheckBlur,
  selectedFuncCount,
  tempShowKeywords,
  connectDropTarget,
  onSelectionBoxDrop,
  tempShowKeywordsInCodingBox,
}) => {
  const acSelContext = useACSelectionBoxContext();
  const [editing, setEditing] = useState(false);
  const [inputText, setInputText] = useState({});
  const infoBoxRef = useRef(null);
  const selectionBoxRef = useRef(null);
  const [opNodeList, setOpNodeList] = useState({});

  const {
    removeSelectedNodes,
    selectAllNodes,
    changeSelectedOperator,
    duplicateSelectedNodes,
    mergeSelectedNodes,
    combineSelectedIntoNetAny,
    setupBulkJoin,
    cancelBulkJoinSetup,
    mergeBulkJoin,
    insertFunctionNodes,
    selectAllOperators,
    changeSelectedOperatorById,
    selectAllTemplateOperatorsByType,
    changeFunctionOfSelectedNodes,
    selectAllFunctions,
  } = AutocodeSelectionAction(acSelContext.state, acSelContext.dispatch);

  const {
    editMode,
    editFolderDetails,
    showBulkJoinBtn,
    toggleDragDrop,
  } = useACEditContext();

  // temporary until popup is written
  const applyFunc = (
    functionName,
    applyTo,
    paramObj,
    isSelected,
    operator = {}
  ) => {
    if (isSelected) {
      changeFunctionOfSelectedNodes(functionName, false, true, paramObj);
    } else {
      const selectedNodes = _.filter(
        acSelContext.state.rootNode.children,
        n => n.selected === true
      );
      insertFunctionNodes(
        functionName,
        false,
        true,
        {
          // specific to each type
          lower: paramObj.lower,
          upper: paramObj.upper,
          operator,
        },
        selectedNodes,
        applyTo === 1,
        cnsts.AC_ROOT_NODE_ID,
        -1
      );
    }
  };
  // before applying netany checking if children has netany
  const getChildrenNetAny = node => {
    let nerarr = false;
    const checkChildren = node => {
      if (node.type === AC_NODETYPE_FUNC) {
        const withoutNetany = [];
        if (node.data.funcName === 'net any') {
          nerarr = true;
        } else {
          withoutNetany.push(node);
        }
      }
    };
    checkChildren(node);
    return nerarr;
  };

  const RemoveNetAnyFromChildrenCheck = topNode => {
    let nodeHasChild = false;
    if (!topNode) return false;
    topNode.children.forEach(n => {
      if (n.selected && getChildrenNetAny(n)) {
        nodeHasChild = true;
      }
    });
    return nodeHasChild;
  };

  const handleSelectionOperatorClicked = operator => {
    if (selectedNodesCount().length > 0) {
      if (
        (operator.label === cnsts.AC_OPERATORS.type.and.label ||
          operator.label === cnsts.AC_OPERATORS.type.or.label) &&
        CanCombineSelectedAsNetAny(acSelContext.state.rootNode)
      ) {
        // Net any which part of template 8 are being applied only for AND oeprator and
        // if all the selected nodes are of net any type
        // then check if its children is netany change that to AND operator
        if (RemoveNetAnyFromChildrenCheck(acSelContext.state.rootNode)) {
          combineSelectedIntoNetAny(operator, true, -1);
        } else {
          // if all the selected nodes are of net any type but child is not a netany
          applyFunc('net any', 0, { from: 0, to: 0 }, false, operator);
        }
      } else if (groupWithOn === true) {
        mergeBulkJoin(operator, true, -1);
      } else {
        mergeSelectedNodes(operator, true, -1);
      }
    } else if (selectedOperatorCount().count > 0)
      changeSelectedOperator(operator);
    setGroupWithOn(false);
  };

  const handleNotFuncClicked = () => {
    const selectedNodes = _.filter(
      acSelContext.state.rootNode.children,
      n => n.selected === true
    );

    selectedNodes.forEach(sn => {
      insertFunctionNodes(
        'NOT',
        false,
        true,
        null, // extra data for a function
        [sn]
      );
    });
  };

  const handleSumFuncClicked = () => {
    applyFunc('SUM', 0, { from: 0, to: 0 }, false);
  };

  const toggleSetGroupWith = () => {
    if (groupWithOn === true) {
      cancelBulkJoinSetup();
      acSelContext.trackerFn({ action: AUTOCODE_CANCEL_BULK_JOIN });
      setGroupWithOn(false);
      selectAllNodes(false);
      // } else if (selectedNodesCount().length >= 1) {
    } else {
      selectAllNodes(true);
      setupBulkJoin();
      acSelContext.trackerFn({ action: AUTOCODE_SETUP_BULK_JOIN });
      setGroupWithOn(true);
    }
  };

  const trackActions = (title, index) => {
    if (title === cnsts.AC_COUNT_TITLE) {
      if (index === 0) {
        acSelContext.trackerFn({
          action: AUTOCODE_FUNCTION_COUNT_EXACTLY_RADIO_CLICK,
        });
      } else {
        acSelContext.trackerFn({
          action: AUTOCODE_FUNCTION_COUNT_MINMAX_RADIO_CLICK,
        });
      }
    }
  };

  const buildInfoBox = () => {
    const selectedFuncNode = selectedFuncCount().node[0];
    let selectedFuncObj = {
      rangeItem: { lower: 1, upper: selectedNodesCount().length },
      funcSele: null,
    };
    const funcArr =
      selectedFuncCount().count > 0
        ? funcsInsideFX(moduleStudy, selectedFuncCount().node)
        : funcsInsideFX(moduleStudy, selectedNodesCount());
    if (
      selectedFuncCount().count > 0 &&
      selectedFuncNode.data.funcName === 'COUNT'
    ) {
      selectedFuncObj = {
        name: selectedFuncNode.data.funcName,
        index:
          selectedFuncNode.data.params.from === selectedFuncNode.data.params.to
            ? 1
            : 0,
        funcSele: false,
        rangeItem: {
          lower: selectedFuncNode.data.params.lower,
          upper: selectedFuncNode.data.params.upper,
        },
      };
    } else if (
      selectedFuncCount().count > 0 &&
      selectedFuncNode.data.funcName !== 'SUM' &&
      selectedFuncNode.data.funcName !== 'NOT' &&
      selectedFuncNode.data.funcName !== 'COUNT' &&
      selectedFuncNode.data.funcName !== 'net any'
    ) {
      const filtered = funcArr.filter(
        item => item.funcName === selectedFuncNode.data.funcName
      );
      selectedFuncObj = {
        name: filtered[0].funcName,
        index: filtered[0].key,
        funcSele: true,
        rangeItem: { lower: 1, upper: 1 },
      };
    }
    const ibp = [];
    ibp.push(
      <ACFunctionBoxPanel
        selectedNodesCount={selectedNodesCount}
        functionsOptionLIst={funcArr}
        applyFunc={applyFunc}
        infoBoxRef={infoBoxRef}
        selectedFuncCount={selectedFuncCount}
        selectedFuncObj={selectedFuncObj}
        isSelected={selectedFuncCount().count > 0}
        trackActions={trackActions}
      />
    );
    return ibp;
  };

  useEffect(() => {
    if (editing) {
      inputText.focus();
      inputText.select();
    }
  }, [inputText]);

  const applyOrToAll = e => {
    if (e) {
      selectAllTemplateOperatorsByType(
        selectedOperatorCount().template,
        cnsts.AC_OPERATORS.type.or
      );
      if (selectionBoxRef && selectionBoxRef.current)
        selectionBoxRef.current.focus();
    } else {
      selectAllOperators(false);
      changeSelectedOperatorById(
        opNodeList.data.type,
        opNodeList.id,
        !opNodeList.data.selected
      );
    }
  };

  const handleOnFocus = v => {
    if (!v) {
      setCheckBlur(v);
    } else
      setTimeout(() => {
        setCheckBlur(v);
      }, 500);
  };

  const selectedOpNode = node => setOpNodeList(...opNodeList, node);

  const itemBoxForApplyAll = () => {
    return (
      <React.Fragment>
        <GenericCheckBox
          onFocus={() => handleOnFocus(false)}
          onBlur={() => handleOnFocus(true)}
          onCheck={e => applyOrToAll(e.target.checked)}
          value="Apply to All ORs"
          label="Apply to All <b>ORs</b>"
          classname="ac-selection-box-item-box-selectAll"
        />
      </React.Fragment>
    );
  };

  const acItemBox = () => {
    return (
      <div
        className="ac-selection-box-item-box"
        style={
          selectedNodesCount().length > 0
            ? { background: '#F3F7FC' }
            : { background: 'transparent' }
        }
      >
        <span className="item-count">{`${numTopLevelNodes()} Items`}</span>
        {selectedNodesCount().length === 0 && (
          <button
            type="button"
            className={
              numTopLevelNodes() === 0
                ? 'empty-selection-box'
                : 'select-all-label'
            }
            onClick={() => {
              selectAllNodes(true, false);
              setTimeout(() => {
                if (selectionBoxRef && selectionBoxRef.current)
                  selectionBoxRef.current.focus();
              }, 0);
            }}
          >
            Select All
          </button>
        )}
      </div>
    );
  };

  const enableBJ = () => {
    let bj =
      numTopLevelNodes() > 1 &&
      !AC_TEMPLATE_2_7_8_ARR.includes(selectedOperatorCount().template) &&
      !_.isEqual(
        selectedOperatorCount().label,
        cnsts.AC_OPERATORS.type.or.label
      );

    // special casese
    bj =
      bj &&
      !_.every(
        acSelContext.state.rootNode.children,
        c =>
          c.type === cnsts.AC_NODETYPE_OPERATOR ||
          c.type === cnsts.AC_NODETYPE_FUNC
      );
    return bj;
  };

  const enableSUMButton = () => {
    //  for all selectedNode datatypes have to be numeric
    // or a func that is already a sum
    const selectedNodes = _.filter(
      acSelContext.state.rootNode.children,
      n =>
        n.selected === true &&
        ((n.type === cnsts.AC_NODETYPE_DATAPOINT && n.data.midpoint) ||
          (n.type === cnsts.AC_NODETYPE_FUNC &&
            (n.data.funcName === 'SUM' || n.data.funcName === 'VOL')))
    );

    return selectedNodes.length >= 2;
  };

  const selectionButtons = () => {
    const bulkJoinButton = () => {
      return (
        <GenericButton
          name={groupWithOn ? 'Cancel' : 'Bulk Join'}
          btnStyle={enableBJ() ? 'func-box-btn' : 'disable-label'}
          onBtnClick={() => enableBJ() && toggleSetGroupWith()}
        />
      );
    };

    const showANDNOT = () =>
      selectedNodesCount().length !== 1 || // more than two nodes selected
      selectedOperatorCount().count > 0; // an operator selected

    const operatorButtons = () => {
      return (
        <React.Fragment>
          <GenericButton
            name="AND"
            btnStyle={
              selectedNodesCount().length > 1
                ? 'func-box-btn'
                : !selectedOperatorCount().count > 0
                ? 'disable-label'
                : !AC_TEMPLATE_2_7_8_ARR.includes(
                    selectedOperatorCount().template
                  )
                ? _.isEqual(
                    selectedOperatorCount().label,
                    cnsts.AC_OPERATORS.type.and.label
                  )
                  ? 'func-box-btn highlight-label'
                  : 'func-box-btn'
                : _.isEqual(
                    selectedOperatorCount().label,
                    cnsts.AC_OPERATORS.type.or.label
                  )
                ? 'func-box-btn'
                : _.isEqual(
                    selectedOperatorCount().label,
                    cnsts.AC_OPERATORS.type.and.label
                  )
                ? 'func-box-btn highlight-label'
                : 'func-box-btn'
            }
            onBtnClick={() =>
              handleSelectionOperatorClicked(cnsts.AC_OPERATORS.type.and)
            }
          />
          <GenericButton
            name="OR"
            btnStyle={
              selectedNodesCount().length > 1 ||
              selectedOperatorCount().count > 0
                ? selectedOperatorCount().count > 0 &&
                  _.isEqual(
                    selectedOperatorCount().label,
                    cnsts.AC_OPERATORS.type.or.label
                  )
                  ? 'func-box-btn highlight-label'
                  : 'func-box-btn'
                : 'disable-label'
            }
            onBtnClick={() =>
              handleSelectionOperatorClicked(cnsts.AC_OPERATORS.type.or)
            }
          />
          {showANDNOT() && (
            <GenericButton
              name="AND NOT"
              btnStyle={`btn-nots ${
                selectedNodesCount().length > 1
                  ? 'func-box-btn'
                  : !selectedOperatorCount().count > 0
                  ? 'disable-label'
                  : !AC_TEMPLATE_2_7_8_ARR.includes(
                      selectedOperatorCount().template
                    )
                  ? _.isEqual(
                      selectedOperatorCount().label,
                      cnsts.AC_OPERATORS.type.andnot.label
                    )
                    ? 'func-box-btn highlight-label'
                    : 'func-box-btn'
                  : _.isEqual(
                      selectedOperatorCount().label,
                      cnsts.AC_OPERATORS.type.or.label
                    )
                  ? 'func-box-btn'
                  : _.isEqual(
                      selectedOperatorCount().label,
                      cnsts.AC_OPERATORS.type.andnot.label
                    )
                  ? 'func-box-btn highlight-label'
                  : 'func-box-btn'
              }
              `}
              onBtnClick={() =>
                handleSelectionOperatorClicked(cnsts.AC_OPERATORS.type.andnot)
              }
            />
          )}
        </React.Fragment>
      );
    };

    const actionButtons = () => {
      return (
        <React.Fragment>
          {selectedNodesCount().length === 1 && (
            <GenericButton
              name="NOT"
              btnStyle="func-box-btn btn-nots"
              onBtnClick={handleNotFuncClicked}
            />
          )}
          <GenericButton
            name="SUM"
            btnStyle={enableSUMButton() ? 'func-box-btn' : 'disable-label'}
            onBtnClick={() => enableSUMButton() && handleSumFuncClicked()}
          />
          <div
            className={
              selectedNodesCount().length > 0 || selectedFuncCount().count > 0
                ? 'func-box-div'
                : 'func-box-disable-div'
            }
          >
            {/* fx  and arrow is combined  */}
            <span
              className={
                selectedNodesCount().length > 0 || selectedFuncCount().count > 0
                  ? 'ibox-ellipsis-icon'
                  : 'ibox-ellipsis-disable-icon'
              }
              onClick={() =>
                acSelContext.trackerFn({ action: AUTOCODE_FUNCTION_BTN_CLICK })
              }
            >
              <InfoBox
                ref={infoBoxRef}
                Panels={buildInfoBox()}
                Positions={['bottom']}
                updatePanels={buildInfoBox}
                Align="end"
                containerClassName={
                  selectedNodesCount().length === 0
                    ? 'func-box-btn ib-popover-ac-container'
                    : 'func-box-btn ib-popover-ac-container'
                }
                setCheckBlur={setCheckBlur}
              />
            </span>
          </div>
          <button
            type="button"
            className={
              selectedNodesCount().length === 0
                ? 'union-disable-icon'
                : 'func-box-btn union-icon'
            }
            onClick={() => duplicateSelectedNodes()}
          />
          <button
            type="button"
            className={
              selectedNodesCount().length > 0 ||
              selectedOperatorCount().count > 0 ||
              selectedFuncCount().count > 0
                ? 'func-box-btn delete-icon'
                : 'delete-disable-icon'
            }
            onClick={() => handleDeleteClicked()}
          />
        </React.Fragment>
      );
    };

    return !groupWithOn ? (
      <React.Fragment>
        {showBulkJoinBtn && bulkJoinButton()}
        {operatorButtons()}
        {actionButtons()}
      </React.Fragment>
    ) : (
      <React.Fragment>
        {operatorButtons()}
        {showBulkJoinBtn && bulkJoinButton()}
      </React.Fragment>
    );
  };

  const renderMainSelectionBox = () => {
    return (
      <div className="ac-selection-box-main-container">
        {AC_TEMPLATE_2_7_8_ARR.includes(selectedOperatorCount().template) &&
        selectedOperatorCount().isSameOPSelected &&
        selectedOperatorCount().label &&
        _.isEqual(
          selectedOperatorCount().label,
          cnsts.AC_OPERATORS.type.or.label
        ) &&
        checkApplyAllOr()
          ? itemBoxForApplyAll()
          : acItemBox()}
        <div
          className="ac-selection-box-func-box"
          style={
            selectedNodesCount().length > 0 || selectedOperatorCount().count > 0
              ? { background: '#F3F7FC' }
              : { background: 'transparent' }
          }
        >
          {selectionButtons()}
        </div>
        {numTopLevelNodes() === 1 &&
          (acSelContext.state.rootNode.children[0].type ===
            AC_NODETYPE_DATAPOINT ||
            acSelContext.state.rootNode.children[0].type ===
              AC_NODETYPE_OPERATOR ||
            acSelContext.state.rootNode.children[0].type ===
              AC_NODETYPE_FUNC) && (
            <div
              className={
                acSelContext.state.rootNode.children[0].useLabelOverride
                  ? 'ac-selection-box-text-box-edited'
                  : 'ac-selection-box-text-box'
              }
            >
              {editing ? (
                <GenericEditWithCancelSave
                  setEditing={setEditing}
                  initValue=""
                  saveInput={textValue => {
                    setEditing(false);
                    tempOverridelabels(
                      acSelContext.state.rootNode.children[0].id,
                      textValue
                    );
                  }}
                  setInputText={setInputText}
                />
              ) : (
                <span
                  className={
                    acSelContext.state.rootNode.children[0].useLabelOverride
                      ? 'ac-selection-box-text-box-edited-content'
                      : 'ac-selection-box-text-box-content'
                  }
                  onClick={() => setEditing(true)}
                >
                  {editMode &&
                  editFolderDetails.length === 1 &&
                  !acSelContext.state.rootNode.children[0].useLabelOverride
                    ? editFolderDetails[0].title
                    : acSelContext.state.rootNode.children[0].toLabelString(
                        acSelContext.state.partsToShow,
                        true
                      )}
                </span>
              )}
            </div>
          )}
        <AutocodeCustomDragLayer
          items={acSelContext.state.rootNode.children.filter(
            item => item.selected
          )}
          type={AC_GRID.AC_GRID_2}
        />
        <AutocodeSelectionBoxItems
          selectionBoxRef={selectionBoxRef}
          definitionTab={definitionTab}
          setGroupWithOn={setGroupWithOn}
          handleDeleteClicked={handleDeleteClicked}
          selEditLabelBoxactive={numTopLevelNodes() === 1}
          infoBoxRef={infoBoxRef}
          selectionboxData={acSelContext.state.rootNode.children.filter(
            item => item.selected
          )}
          removeSelectedNodes={removeSelectedNodes}
          selectedOpNode={selectedOpNode}
          tempShowKeywords={tempShowKeywords}
          tempShowKeywordsInCodingBox={tempShowKeywordsInCodingBox}
          onSelectionBoxDrop={onSelectionBoxDrop}
          selectAllNodes={selectAllNodes}
          selectAllFunctions={selectAllFunctions}
          selectAllOperators={selectAllOperators}
        />
      </div>
    );
  };

  return toggleDragDrop
    ? connectDropTarget(renderMainSelectionBox())
    : renderMainSelectionBox();
};

AutocodeMainSelectionBox.propTypes = {
  numTopLevelNodes: PropTypes.func,
  selectedNodesCount: PropTypes.func,
  selectedOperatorCount: PropTypes.func,
  handleDeleteClicked: PropTypes.func,
  definitionTab: PropTypes.func,
  tempOverridelabels: PropTypes.func,
  groupWithOn: PropTypes.bool,
  setGroupWithOn: PropTypes.func,
  selectedFuncCount: PropTypes.func,
  moduleStudy: PropTypes.shape({}),
  setCheckBlur: PropTypes.bool,
  tempShowKeywords: PropTypes.func,
  tempShowKeywordsInCodingBox: PropTypes.func,
};

export default dropTarget(
  [AC_GRID.AC_GRID_1, AC_GRID.AC_DEFINITIONS],
  target,
  collect
)(AutocodeMainSelectionBox);
